import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon } from './icons';

/**
 * A finite, deliberately ordered horizontal row (spec: max 2–3 rows, no endless feed). Items are
 * reachable with Tab; the scroll buttons are only shown while there is more to see in that
 * direction, so no control is ever decorative.
 */
export function Rail({
  title,
  description,
  children,
  emptyMessage,
  previousLabel,
  nextLabel,
  action,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  emptyMessage: string;
  previousLabel: string;
  nextLabel: string;
  /** Optional link shown next to the heading, e.g. "See all". */
  action?: ReactNode;
}) {
  const id = useId();
  const list = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const items = Children.toArray(children);

  const measure = useCallback(() => {
    const el = list.current;
    if (!el) return;
    setEdges({
      start: el.scrollLeft <= 1,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = list.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, items.length]);

  const scroll = (direction: -1 | 1) => {
    const el = list.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <section className="ui-rail" aria-labelledby={`${id}-title`}>
      <div className="ui-rail__header">
        <div>
          <h2 className="ui-rail__title" id={`${id}-title`}>
            {title}
          </h2>
          {description && <p className="ui-rail__description">{description}</p>}
        </div>
        <div className="ui-rail__tools">
          {action}
          {!edges.start && (
            <button
              type="button"
              className="ui-rail__scroll"
              onClick={() => scroll(-1)}
              aria-controls={`${id}-list`}
            >
              <Icon name="chevron-left" />
              <span className="ui-visually-hidden">{previousLabel}</span>
            </button>
          )}
          {!edges.end && (
            <button
              type="button"
              className="ui-rail__scroll"
              onClick={() => scroll(1)}
              aria-controls={`${id}-list`}
            >
              <Icon name="chevron-right" />
              <span className="ui-visually-hidden">{nextLabel}</span>
            </button>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="ui-rail__empty">{emptyMessage}</p>
      ) : (
        <ul className="ui-rail__list" id={`${id}-list`} ref={list} onScroll={measure}>
          {items.map((child, index) => (
            <li className="ui-rail__item" key={isValidElement(child) ? child.key : index}>
              {child}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
